import { ChangeDetectionStrategy, Component, OnInit, TemplateRef } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { Sort } from "@angular/material/sort";
import { pushError } from "@app/_helpers/globalErrorHandler";
import { logger } from "@app/_helpers/logger";
import { trackId } from "@app/_helpers/ngFor";
import { isTimeLockedForDate, isTimeLockedForDateFuture } from "@app/_helpers/timeLock";
import { useProjectFind, useUserFind } from "@app/_helpers/useUtils";
import { createRxValue } from "@app/_helpers/utils";
import { MaterialScale } from "@app/animations/fade";
import { AppService } from "@app/app.service";
import { parseDurationSeconds } from "@app/components/duration-input-control/duration-input-utils";
import { TimeBulkEditDialogComponent } from "@app/components/time-bulk-edit-dialog/time-bulk-edit-dialog.component";
import { DeletionUndoService } from "@app/services/deletion/deletion-undo.service";
import { EditUndoService } from "@app/services/edit-undo/edit-undo.service";
import type { TimeBulkEditDialogCloseResult } from "@app/services/edit-undo/edit-undo.types";
import { MixedTimesSyncService } from "@app/services/mixed-times-sync.service";
import { RecordToolbarService } from "@app/shared/record-toolbar/record-toolbar.service";
import { TRange } from "@app/shared/time-range-picker/time-range-constants";
import { type EntityStore } from "@datorama/akita";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import { MyTimesQuery, MyTimesService, ProjectsQuery, Time, TimesService, UserSettingsQuery } from "@timeghost/web-api";
import { format, parse, startOfDay } from "date-fns/esm";
import produce from "immer";
import { combineLatest, firstValueFrom } from "rxjs";
import { filter, map, shareReplay } from "rxjs/operators";
import { firstBy } from "thenby";
import { ReportsService } from "../reports.service";
import { useTransformReportItemToTime, useTransformTimeToReportItem } from "../utils";

@UntilDestroy()
@Component({
	selector: "tg-detailed-page",
	templateUrl: "./detailed-page.component.html",
	styleUrls: ["./detailed-page.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [MaterialScale],
})
export class DetailedPageComponent implements OnInit {
	isLoading: boolean = false;
	constructor(
		private reports: ReportsService,
		private recordService: RecordToolbarService,
		private appService: AppService,
		private timeService: TimesService,
		private myTimesService: MyTimesService,
		private userSettingsQuery: UserSettingsQuery,
		private projectsQuery: ProjectsQuery,
		private dialog: MatDialog,
		private translate: TranslateService,
		private myTimesQuery: MyTimesQuery,
		private deletionService: DeletionUndoService,
		private editUndoService: EditUndoService,
		private mixedTimesSyncService: MixedTimesSyncService
	) {}
	readonly selected = createRxValue<string[]>([]);
	readonly editMode = createRxValue(false);
	readonly timeLoading$ = this.reports.timeLoading$;
	readonly time$stats = this.reports.data$stats;
	readonly showAmounts$ = this.reports.showAmounts$;
	get activeSort() {
		return this.reports.detailedSort.value;
	}
	get timeLoading() {
		return this.reports.timeLoading;
	}
	readonly findUser = useUserFind();
	readonly transformReportItemToTime = useTransformReportItemToTime();
	readonly entries$ = combineLatest([
		this.reports.data$filtered.asObservable(),
		this.selected.asObservable(),
		this.reports.detailedSort.asObservable(),
	]).pipe(
		map(([_entries, selected, sort]) => {
			let entries = _entries.map((reportItem) => {
				const transformedItem = this.transformReportItemToTime(reportItem);
				return {
					...reportItem,
					projectArchived: transformedItem.project.completed,
					editable: transformedItem.currentUserCanEdit,
					currentUserCanEdit: transformedItem.currentUserCanEdit,
					raw: transformedItem,
					checked: selected.findIndex((t) => t === reportItem.id) !== -1,
				};
			});
			const sortBy = sort.direction === "desc" ? "desc" : "asc";
			if (sort?.active) {
				if (sort.active === "name") entries = entries.sort(firstBy("name", sortBy));
				else if (sort.active === "user")
					entries = entries.sort(firstBy((x) => this.findUser(x.userId)?.name ?? "", sortBy));
				else if (sort.active === "duration") entries = entries.sort(firstBy((x) => ~~x.duration, sortBy));
				else if (sort.active === "amount") entries = entries.sort(firstBy("amount", sortBy));
				else if (sort.active === "time") entries = entries.sort(firstBy("start", sortBy));
				else if (sort.active === "project") entries = entries.sort(firstBy((x) => x.projectName, sortBy));
				else if (sort.active === "task") entries = entries.sort(firstBy((x) => x.taskName, sortBy));
				else if (sort.active === "billable") entries = entries.sort(firstBy("billable", sortBy));
				else if (sort.active === "tags") entries = entries.sort(firstBy((x) => x.tags?.length, sortBy));
			}
			return entries;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);
	readonly stats$ = this.reports.stats$;
	get range() {
		return this.reports.range.value;
	}
	set range(val: TRange) {
		this.reports.onRangeUpdate(val);
	}
	readonly trackId = trackId;
	toggleSelect(id: string, checked: boolean) {
		if (checked) this.selected.value = [...this.selected.value, id].uniq();
		else this.selected.value = this.selected.value.filter((x) => x !== id);
	}
	ngOnInit(): void {
		this.deletionService.deleted
			.pipe(
				filter((ev) => ev.type === "timeEntries"),
				untilDestroyed(this)
			)
			.subscribe(() => {
				this.selected.value = [];
				this.reports.reload({ silent: true });
			});
		this.deletionService.restored
			.pipe(
				filter((ev) => ev.type === "timeEntries"),
				untilDestroyed(this)
			)
			.subscribe((ev) => {
				if (ev.entryIds?.length) {
					this.selected.value = ev.entryIds.uniq();
				}
				this.reports.reload({ silent: true });
			});
		this.editUndoService.reverted.pipe(untilDestroyed(this)).subscribe(() => {
			this.reports.reload({ silent: true });
		});
		this.reports.lastContingentUrl.update(null);
	}
	updateItem(ev: Time) {
		if (!this.reports.testProjectTimeFilter(ev))
			this.reports.projecthours.update((state) => {
				state = state.filter((x) => x.id !== ev.id);
				return state;
			}, true);
	}
	changeDataSort(sort: Sort) {
		this.reports.detailedSort.value = sort;
	}
	addEntry() {
		return this.recordService.openCreateDialog();
	}
	async onBeforeUpdateTime(updatePayload?: { value: any; entity: Time; inputMode: "range" | "duration" }) {
		if (!updatePayload?.value) return;
		const {
			start: formattedStart,
			end: formattedEnd,
			duration: formattedDuration,
			stateVisibleMode,
			date,
		}: {
			start: string;
			end: string;
			duration: string;
			date: Date;
			stateVisibleMode: boolean;
		} = updatePayload.value;
		const inputMode = stateVisibleMode ? "range" : "duration";
		const start = parse(formattedStart, "HH:mm", date),
			end = parse(formattedEnd, "HH:mm", date),
			duration = parseDurationSeconds(formattedDuration);
		if (start > end) {
			pushError("record-toolbar.start-lt-end");
			return;
		}
		const userSettings = this.userSettingsQuery.getValue();
		const blockTimes = userSettings.workspace.settings?.blockTimes;
		const project = this.projectsQuery.getAll().find((x) => x.id == updatePayload?.entity.project.id);
		if (isTimeLockedForDate(this.userSettingsQuery.getValue(), project, start)) {
			this.appService.notifier.show({
				message: this.translate.instant("errors.blockTimesPast", {
					value: blockTimes?.period,
					range: blockTimes?.range,
				}),
				type: "error",
			});
			return;
		}
		if (isTimeLockedForDateFuture(this.userSettingsQuery.getValue(), project, end)) {
			this.appService.notifier.show({
				message: this.translate.instant("errors.blockTimesFuture", {
					value: blockTimes?.futurePeriod,
					range: blockTimes?.futureRange,
				}),
				type: "error",
			});
			return;
		}
		const time = this.reports.projecthours.value.find(
			(x) => updatePayload?.entity?.id && x.id === updatePayload.entity.id
		);
		if (!this.myTimesQuery.hasEntity(time.id)) {
			await this.myTimesService.adapter
				.post<[Time]>("/get/times", {
					$filter: `id eq '${time.id}'`,
				})
				.toPromise()
				.then((times) => {
					if (times) (this.myTimesQuery.__store__ as EntityStore).upsertMany(times);
				})
				.catch((err) => {
					logger.error(err);
				});
		}
		const entity = this.myTimesQuery.getEntity(time.id);
		const original = structuredClone(entity);
		return this.myTimesService
			.update(
				produce(entity, (draft) => {
					if (inputMode === "range") {
						draft.start = start.toISOString();
						draft.end = end.toISOString();
					} else if (inputMode === "duration") {
						draft.start = startOfDay(start.getTime()).toISOString();
						draft.end = draft.start;
						draft.timeDiff = duration;
					}
					// @ts-ignore
					draft.inputMode = inputMode;
					return draft;
				})
			)
			.toPromise()
			.then(([updated]) => {
				this.appService.notifySuccess("success.updated");
				if (updated) {
					this.mixedTimesSyncService.handleProjectTimeDialogClose(updated, original);
					this.reports.updateProjectItem(updated);
				}
			})
			.catch(() => {
				pushError(
					this.userSettingsQuery.getValue().id === time.userId
						? "errors.time.user"
						: "errors.time.another-user"
				);
				return this.reports.reload();
			});
	}
	private findProject = useProjectFind();
	readonly transformItemToReportItem = useTransformTimeToReportItem();
	editSelected() {
		const ws = this.userSettingsQuery.getValue()?.workspace;
		const times = this.reports.projecthours.value;
		const mappedTimes = this.selected.value
				.uniq()
				.map((x) => times.find((y) => y.id === x))
				.map((x) => this.transformReportItemToTime(x)),
			isMixedProject = mappedTimes.uniqBy((x) => x.project.id).length > 1,
			isMixedDate = mappedTimes.uniqBy((x) => startOfDay(new Date(x.start)).getTime()).length > 1,
			isMixedBillable = mappedTimes.uniqBy((x) => x.billable).length > 1,
			isMixedTask = mappedTimes.uniqBy((x) => x.task?.id ?? null).length > 1,
			isMixedName = mappedTimes.uniqBy((x) => x.name).length > 1,
			isMixedUser = mappedTimes.uniqBy((x) => x.user.id).length > 1,
			isMixedInputs = mappedTimes.uniqBy((x) => x["inputMode"]).length > 1,
			isMixedTags =
				mappedTimes.uniqBy((x) =>
					x.tags
						?.map((y) => y.id)
						.sort()
						.join("")
				).length > 1,
			isMixed = mappedTimes.uniqBy((x) => x.task?.id ?? null).length > 1;
		const time = mappedTimes.find((d) => d.task?.id) ?? mappedTimes[0];
		const user = !isMixedUser && time?.user.id ? ws.users.find((x) => x.id === time.user.id) : null;
		this.editUndoService.dismissAllPendingUndoToasts();
		return this.dialog
			.open(TimeBulkEditDialogComponent, {
				data: {
					name: isMixedName ? null : time.name,
					project: isMixedProject || !time.project ? null : this.findProject(time.project.id),
					task: isMixedTask ? null : (time.task ?? null),
					start: isMixed ? null : format(new Date(time.start), "HH:mm"),
					end: isMixed ? null : format(time.end ? new Date(time.end) : new Date(), "HH:mm"),
					timeDiff: isMixedInputs || time["inputMode"] !== "duration" ? null : time.timeDiff,
					date: isMixedDate ? null : startOfDay(new Date(time.start)),
					billable: isMixedBillable ? null : !!time.billable || !!this.findProject(time.project.id)?.billable,
					user: isMixedUser ? null : user,
					tags: isMixedTags ? null : time.tags,
					isMixed,
					canEdit: mappedTimes.every((x) => !!x.currentUserCanEdit),
					entities: mappedTimes,
				},
			})
			.afterClosed()
			.pipe(untilDestroyed(this))
			.subscribe((result: TimeBulkEditDialogCloseResult | undefined) => {
				if (result == null) return;
				this.reports.reload({ silent: true });
				this.editUndoService.offerUndo(result.undoOffer);
			});
	}
	async selectAll() {
		const entries = await firstValueFrom(this.entries$);
		const needSelect = entries
			.filter((x) => x.currentUserCanEdit && !x.projectArchived)
			.map((x) => x.id)
			.uniq();
		this.selected.value = needSelect.length !== this.selected.value?.length ? needSelect : [];
	}
	deleteSelected(ref: TemplateRef<any>) {
		const times = this.reports.projecthours.value;
		const mappedTimes = this.selected.value
			.uniq()
			.map((x) => times.find((y) => y.id === x))
			.map((x) => this.transformReportItemToTime(x))
			.filter((y) => !!y);
		if (!mappedTimes.length) return;
		return this.dialog
			.open(ref, {
				data: {
					entities: mappedTimes,
				},
			})
			.afterClosed()
			.pipe(untilDestroyed(this))
			.subscribe((confirmed: boolean) => {
				if (!confirmed) return;
				this.deletionService.dismissAllPendingUndoToasts();
				this.deletionService.deleteWithUndo({ type: "timeEntries", entries: mappedTimes });
			});
	}
}
